import { useEffect, useRef, useState } from 'react';
import { fetchArtistsOverview, type ArtistsOverviewResponse } from '../../lib/artistsOverviewApi';
import { useAuth } from '../../hooks/useAuth';
import { useLikes } from '../../context/LikesContext';

const AUTH_STORAGE_KEY = '254tracks.auth';

function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.token === 'string' ? parsed.token : null;
  } catch {
    return null;
  }
}

export function useArtistsBootstrap() {
  const [data, setData] = useState<ArtistsOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { token, hydrateFromBootstrap: hydrateAuth } = useAuth();
  const { hydrateFromBootstrap: hydrateLikes } = useLikes();
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (hasLoaded.current) return;
    hasLoaded.current = true;
    const storedToken = token ?? getStoredToken();

    const load = async () => {
      setLoading(true);
      try {
        const payload = await fetchArtistsOverview(storedToken ?? undefined);
        setData(payload);
        setError(null);
        hydrateAuth?.({ user: payload.user, token: storedToken });
        hydrateLikes?.(payload.likes?.artists ?? [], payload.likes?.tracks ?? []);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to load artists.';
        setError(message);
      } finally {
        setLoading(false);
        if (typeof window !== 'undefined') {
          (window as { __ARTISTS_BOOTSTRAP__?: boolean }).__ARTISTS_BOOTSTRAP__ = false;
        }
      }
    };

    void load();
  }, [hydrateAuth, hydrateLikes, token]);

  return { data, loading, error };
}
